import {listTypes} from './enum';
import React, {useCallback, useRef, useState} from 'react';
import {helpPopoverRef} from '../../../../_shared/Help/constants';
import {Flex, Icon, PriorityIcon, Text, Dropdown} from '@priority-software/priority-style-react';
import {highlightText} from '../../../../_legacy/library/utils/highlightText';
import {SearchDropdown} from '../../../../_legacy/FormHeader/components/Search/SearchDropdown';
import List from '../../../../components/Common/List';
import {Footer} from './ModalFooter';
import {showHelp} from '../../common/helpPopover';
import {keys} from '../../../../constants/KeyCodes';
import styles from './style.module.scss';
import {ActionIcon} from '../../../../components/ActionIcon';

type ListProps = {
    items: Array<any>;
    onClose: () => void;
    noResultMsg: string;
    listType: listTypes;
    open: boolean;
};

const visibleResults = 10;

export const SearchableList = (props: ListProps) => {
    const {items, onClose, noResultMsg, open} = props;
    const [searchValue, setSearchValue] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [filteredItems, setFilteredItems] = useState(items);
    const itemsRef = useRef({});
    const helpButtonRef = useRef(null);

    const handlePageDown = useCallback(
        (currentIndex: number) => {
            const lastIndex = filteredItems.length - 1;
            let nextIndex = currentIndex + visibleResults;
            nextIndex = nextIndex > lastIndex ? lastIndex : nextIndex;

            return Promise.resolve({nextSelected: nextIndex});
        },
        [filteredItems]
    );

    const handlePageUp = useCallback(
        (currentIndex: number) => Math.max(currentIndex - visibleResults, 0),
        []
    );

    const handleAccept = useCallback(
        index => {
            onClose();
            filteredItems[index]?.action();
        },
        [filteredItems, onClose]
    );

    const onItemClick = useCallback(
        (event, index) => {
            setSelectedIndex(index);

            if (event.key === keys.ENTER && !helpPopoverRef?.current?.state.open) {
                if (
                    event?.target?.name === 'cancel' ||
                    event?.target?.name === 'help' ||
                    event?.target?.ariaLabel === 'Close'
                ) {
                    event.target.click();
                } else {
                    handleAccept(index);
                }
            }
        },
        [handleAccept]
    );

    const handleInputChange = useCallback(
        value => {
            const searchValue = value.toLowerCase();
            const filteredData =
                value === ''
                    ? items
                    : items.filter(item => item.title.toLowerCase().includes(searchValue));

            setFilteredItems(filteredData);
            setSelectedIndex(0);
            setSearchValue(value);
        },
        [items]
    );

    const onDoubleClick = useCallback(
        (index: number) => {
            onClose();
            filteredItems[index]?.action();
        },
        [onClose, filteredItems]
    );

    const renderItem = ({item, index}) => {
        const {type, title, id} = item;
        return (
            <Dropdown.Item
                divider
                focusOnSelect
                index={index}
                key={id}
                closeOnClick={false}
                onClick={event => onItemClick(event, index)}
                onDoubleClick={() => onDoubleClick(index)}
                onRef={ref => (itemsRef.current[index] = ref)}
            >
                <Flex justifyContent="flex-start">
                    {type && <ActionIcon type={type as number} />}
                    <Text startPadding textColor="primary-700" skin="subtitle-2">
                        {highlightText(title, searchValue)}
                    </Text>
                </Flex>
            </Dropdown.Item>
        );
    };

    const handleShowHelpMessage = useCallback(
        async (index: number) => {
            const anchor = itemsRef.current[index];
            if (anchor) {
                const Fname = filteredItems[index].id;
                await showHelp({anchor, Fname});
            }
        },
        [filteredItems]
    );

    return (
        <SearchDropdown
            open={open}
            circularNavigation
            shouldHandleHomeAndEndPress
            eventTypes={[]}
            closeOnESC={false}
            closeOnBlur={false}
            closeOnClear={false}
            clearOnClose={false}
            withArrowIcon={false}
            withTriggerWrapper={false}
            handlePageDown={handlePageDown}
            handlePageUp={handlePageUp}
            focusedIndex={selectedIndex}
            inputProps={{
                componentClasses: items.length < visibleResults - 1 && styles.hideInput,
                prefixIcon: <Icon iconColor="primary-500" icon={PriorityIcon.searchThin} />,
                size: 'large',
                onChange: handleInputChange,
                value: searchValue
            }}
        >
            <div className={styles.list}>
                {filteredItems.length > 0 ? (
                    <List renderItem={renderItem} data={filteredItems} />
                ) : (
                    <Dropdown.Item disabled>
                        <Text skin="paragraph-2">{noResultMsg}</Text>
                    </Dropdown.Item>
                )}
            </div>
            <Footer
                handleClose={onClose}
                handleAccept={handleAccept}
                helpButtonRef={helpButtonRef}
                handleShowHelpMessage={handleShowHelpMessage}
            />
        </SearchDropdown>
    );
};
